import { FrameworkType } from '../../../shared/model/common.model';
import {
    getAssessmentSequence_getAssessmentSequence as AssessmentSequence,
    getMultiStageReport_getMultiStageReport_overallReportData as OverallReportData,
    getMultiStageReport_getMultiStageReport_programHierarchy as ProgramHierarchy,
    getMultiStageReport_getMultiStageReport_studentsReportData as StudentsReportData,
    getStandardsMetadata_getStandardsMetadata_standards as QuestionsData,
    getStandardsMetadata_getStandardsMetadata as StandardMetadata,
    getUserFeatureAttributes_getUserFeatureAttributes as UserFeatureAttributes,
} from '../../../../../schema/graphql-types';
import { AppState } from '../../core.state';
import { MultiStageReportPlayerData } from '../../model/multi-stage-report-player.model';

export enum Locales {
    EN = 'en',
    ES = 'es',
}

export const DEFAULT_LOCALE = 'en';
export const RBS_TOKEN = 'realize.rbs_token';
export const PROFILE_TOKEN = 'profile.token';
export const LOGIN_TIME = 'profile.logintime';
export const ACCORDION_ANIMATION_DURATION = 50;
export const ENABLE_COMMUNICATION_WITH_RBS_ASSESSMENT_SERVICE_FMS_KEY =
    'enable.communication.with.rbs-assessment-service';

export enum PlacementLevel {
    DNM = 'DNM',
    APP = 'APP',
    MET = 'MET',
    EXC = 'EXC',
}

export enum FeatureSetting {
    On = 'ON',
    Off = 'OFF',
}

export enum SkillDomainKeys {
    OV = 'OV',
}

export enum ViewPortWidth {
    Large = 934,
    Medium = 802,
    Small = 768,
}

export enum ViewPortHeight {
    Smaller = 670,
}

export enum TableColumnCount {
    Two = 2,
    Three = 3,
    Four = 4,
}

export enum DropdownContainerWidth {
    Small = 210,
    Large = 410,
}

export enum SortingOrder {
    Ascending = 'ascending',
    Descending = 'descending',
}

export enum PaginationOrder {
    Forward = 'forward',
    Backward = 'backward',
}

export enum UserRole {
    Student = 'Student',
    Teacher = 'Teacher',
    CustomerAdmin = 'Customer Admin',
}

export enum ProgressBarClass {
    DoesNotMeets = 'progress-bar-filled-does-not-meets',
    Exceeds = 'progress-bar-filled-exceeds',
    MeetsExpectations = 'progress-bar-filled-meets-expectations',
    ApproachingExpectation = 'progress-bar-filled-approaching-expectation',
}

export interface StandardsMetadata {
    [key: string]: StandardMetadata;
}

export interface QuestionsDataWithQuestionNumber {
    questionNumber: number;
    currentQuestion: QuestionsData[];
}

export interface AssessmentDetails {
    programHierarchy: ProgramHierarchy[];
    assignedTo: string;
    dueDate: any;
    title: string;
    grade: string;
}

export interface QuestionPreviewData {
    assessmentSessionId: string;
    questionId?: string;
    questionPosition?: string;
    programNames: string[];
    classId: string;
    currentQuestionNumber?: number;
    totalQuestions?: number;
}

export interface MultiStageAssignmentReportState {
    isLoading: boolean;
    userRole: UserRole;
    languageLocale: Locales;
    isErrorOccurred: boolean;
    overallReportData: ClassOverallReportData;
    studentsReportData: ClassStudentsReportData[];
    userFeatureAttributes: UserFeatureAttributes[];
    assessmentDetails: AssessmentDetails;
    isZeroState: boolean;
    assessmentSequence: AssessmentSequence[];
    standardMetadata: StandardMetadata[];
    isAuthenticated: boolean;
    inputParams: MultiStageReportPlayerData;
    standardsModalPayload: StandardsViewerModalMfeData;
    isStandardModalVisible: boolean;
    metricMeasureData: MetricMeasureData;
    studentsNotCompletedData: StudentReportMetric[];
    metricsClassAverage: string;
    assessmentReportingType: string;
}

export interface ClassStudentsReportData extends StudentsReportData {
    overallReportData?: OverallReportData | null;
    overallScaledScore: number;
    nationalPercentile: number;
    placementLevel: string;
    fullName: string;
}
export interface Report {
    overallReportData: ClassOverallReportData;
    studentsReportData: ClassStudentsReportData[];
    framework: FrameworkType;
    percentileScoreMapping: PercentileScoreMapping;
    studentsNotCompleted: StudentReportMetric[];
    assessmentDetails: AssessmentDetails;
    assignmentId?: string;
    classId?: string;
    itemId?: string;
    itemVersion?: string;
    metricsClassAverage: string;
    assessmentReportingType: string;
}

export interface MultiStageReport {
    getMultiStageReport: Report;
}

export interface ClassOverallReportData extends OverallReportData {
    studentPlacementLevelBreakdown: StudentPlacementLevelBreakdown;
    classPerformanceByDomainData: ClassPerformanceByDomainData[];
}

export interface StudentPerformanceLevelBreakdown {
    atRisk: string[];
    onLevel: string[];
    aboveLevel: string[];
}

export interface StudentPlacementLevelBreakdown {
    exceedExpectations: string[];
    meetsExpectations: string[];
    approachingExpectations: string[];
    doesNotMeetsExpectations: string[];
}

export interface ClassPerformanceByDomainData {
    skillDomainKey: string;
    skillDomainScore: number;
}

export interface MetricMeasureData {
    framework: FrameworkType;
    percentileScoreMapping: Record<string, string>;
}

export interface PercentileScoreMapping {
    percentiles: Percentile[];
}

export interface Percentile {
    key: string;
    value: string;
}

export interface StudentReportMetric {
    studentId: string;
    firstName: string;
    lastName: string;
    studentName?: string;
    score?: string;
}

export interface PercentileRange {
    percentileLow: number;
    percentileHigh: number;
}

export enum AppStoreFeatureName {
    MultiStageAssignmentReport = 'multiStageAssignmentReport',
}

export interface State extends AppState {
    multiStageAssignmentReport: MultiStageAssignmentReportState;
}

export interface StandardsViewerModalStandardsData {
    name: string;
    id: string;
    attributes: {
        description: string | null;
        spanishDescription: string | null;
        title?: string;
        code?: string;
    };
    standardGuid: string;
}
export interface StandardsViewerModalPlayerData {
    locale: string;
    title: string;
    skillIds: string[];
    isMobile?: boolean;
    isMasteryMFE?: boolean;
    externalContentButtonText?: string;
    isStandardAssociatedCTA?: boolean;
}

export interface StandardsViewerModalMfeData {
    playerData?: StandardsViewerModalPlayerData;
    playerDataList?: StandardsViewerModalPlayerData;
    standardsList?: StandardsViewerModalStandardsData[];
    isStandardList?: boolean;
    focusElementId?: string;
}

export interface StandardsModalPayload {
    standards?: QuestionsData[];
    question?: QuestionsDataWithQuestionNumber;
    focusElementId: string;
}

export interface ForecastTextSegments {
    beginning: string;
    likely: string;
    perform: string;
    end: string;
}
